import { API } from '~apis/axios/instance';
import { BusinessEntityBankAccount, BusinessEntityType } from '~apis/business-entity';
import { AccountType } from '~models/bank/enum';
import { TaxInvoiceType } from '~models/tax-invoice';
import { PageRequest } from '~types/request';
import { Page, ResponseBaseWithData } from '~types/response';

export interface BusinessEntityListRequest {
  companyId: string;
  query?:
    | {
        startDate: string;
        endDate: string;
        onlyFavorite: boolean;
        type: 'SALES' | 'PURCHASE' | null;
        searchKeyword?: string;
      }
    | PageRequest;
}

export interface BusinessEntity {
  id: number;
  name: string;
  alias: string;
  memo?: string;
  isFavorite: boolean;
  total: ITotal;
  sales: ISales;
  purchase: IPurchase;
  balanceSettings: BalanceSettings;
}
interface ITotal {
  salesBalance: number;
  purchaseBalance: number;
  sumBalance: number;
}
interface ISales {
  salesInvoiceAmount: number;
  salesTransactionAmount: number;
  salesBalance: number;
}
interface IPurchase {
  purchaseInvoiceAmount: number;
  purchaseTransactionAmount: number;
  purchaseBalance: number;
}

interface BusinessEntityListResponse extends Page<BusinessEntity> {}

export const getBusinessEntities = (params: BusinessEntityListRequest) => {
  return API.get<ResponseBaseWithData<BusinessEntityListResponse>>(
    `/api/v2/companies/${params.companyId}/business-entities/contracts`,
    {
      params: {
        ...params.query,
      },
    },
  );
};

export interface BusinessEntitySummaryRequest {
  companyId: string;
  query: {
    startDate: string;
    endDate: string;
  };
}

export const getBusinessEntitiesSummary = (params: BusinessEntitySummaryRequest) => {
  return API.get<
    ResponseBaseWithData<{
      totalSalesInvoiceAmount: number;
      totalSalesTransactionAmount: number;
      totalPurchaseInvoiceAmount: number;
      totalPurchaseTransactionAmount: number;
    }>
  >(`/api/v2/companies/${params.companyId}/business-entities/contracts-summary`, {
    params: params.query,
  });
};

export interface BusinessEntityDetailRequest {
  companyId: string;
  businessEntityId: number;
  startDate: string;
  endDate: string;
}

export interface BusinessEntityDetailForContract {
  name: string;
  alias: string;
  synonyms: string[];
  representatives: {
    id: number;
    name: string;
  }[];
  businessRegNo: string;
  startDate: string;
  endDate: string;
  salesInvoiceAmount: number;
  salesTransactionAmount: number;
  purchaseInvoiceAmount: number;
  purchaseTransactionAmount: number;
  salesBalance: number;
  purchaseBalance: number;
  salesSettlementHistories: SettlementHistoriesItem[];
  purchaseSettlementHistories: SettlementHistoriesItem[];
  representativeNames: string[];
  showBalance: boolean;
  isFavorite: boolean;
  openingYearMonth?: string;
  openingSalesBalance?: number;
  openingPurchaseBalance?: number;
}

export type SettlementType = 'INVOICE' | 'TRANSACTION' | 'INVOICE_ADJ';
export interface SettlementHistoriesItem {
  dataId: string;
  createdAt: string;
  title: string;
  type: SettlementType;
  amount: number;
  balance: number;
  transactionInfo?: {
    transactionAmount: number;
    accountId: number;
    accountType: AccountType;
  };
  invoiceInfo?: {
    issueId?: number;
    isReserved?: boolean;
    invoiceType: TaxInvoiceType;
  };
}

export const getBusinessEntityDetailForContracts = (params: BusinessEntityDetailRequest) => {
  return API.get<ResponseBaseWithData<BusinessEntityDetailForContract>>(
    `/api/v2/companies/${params.companyId}/business-entities/${params.businessEntityId}/contracts`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
      },
    },
  );
};

export interface BusinessEntityDetail {
  id: number;
  companyId: string;
  businessRegNumber: string;
  name: string;
  alias: string;
  memo?: string;
  members: IMembersItem[];
  address: string;
  phoneNumber: string;
  industry: string;
  registrationCertificateUrl: string;
  isFavorite: boolean;
  synonyms: string[];
  bzdNo: string;
  btpNo: string;
  taxRegistrationNo: string;
  taxContacts: ITaxContactsItem[];
  contractBalanceSetting: IContractBalanceSetting;
  taxInvoiceManagers: IMembersItem[];
  createdAt: string;
  updatedAt: string;
  sameNames: string[];
  businessEntityLabel: string;
  bankAccounts: BusinessEntityBankAccount[];
  type: BusinessEntityType;
  isCardOrganization: boolean;
}
export interface IMembersItem {
  id: number;
  name: string;
  type: string;
  position: string;
  department: string;
  phoneNumber: string;
  email: string;
  isTaxInvoiceManager: boolean;
  createdAt: string;
  updatedAt: string;
}
interface ITaxContactsItem {
  name: string;
  email: string;
}
interface IContractBalanceSetting {
  showBalance: boolean;
  openingYearMonth: string;
  openingSalesBalance: number;
  openingPurchaseBalance: number;
}

export const getBusinessEntityDetail = ({ companyId, id }: { companyId: string; id: number }) => {
  return API.get<ResponseBaseWithData<BusinessEntityDetail>>(
    `/api/v2/companies/${companyId}/business-entities/${id}`,
  );
};

export interface TaxInvoiceManager {
  id?: number;
  name: string;
  email: string;
}

export interface UpdateInfo {
  id: number;
  name: string;
  alias?: string;
  memo?: string;
  businessRegNo?: string | null;
  representatives: {
    id?: number | null;
    name: string;
  }[];
  address?: string;
  btpNo?: string;
  bzdNo?: string;
  taxRegistrationNo?: string;
  taxInvoiceManagers?: TaxInvoiceManager[];
  bankAccounts: BusinessEntityBankAccount[];
  type?: BusinessEntityType;
}

export const updateBusinessEntity = ({
  companyId,
  request,
}: {
  companyId: string;
  request: UpdateInfo;
}) => {
  return API.put(`/api/v3/companies/${companyId}/business-entities/${request.id}`, {
    data: request,
  });
};

export function setFavoriteBusinessEntity({
  companyId,
  businessEntityId,
  isFavorite,
}: {
  companyId: string;
  businessEntityId: number;
  isFavorite: boolean;
}) {
  return API.patch(
    `/api/v2/companies/${companyId}/business-entities/${businessEntityId}/favorite?isFavorite=${isFavorite}`,
    {
      data: { isFavorite },
    },
  );
}

export const deleteOrMergeBusinessEntity = ({
  companyId,
  request,
}: {
  companyId: string;
  request: {
    targetId: number;
    mergeToId?: number;
  };
}) => {
  return API.post(`/api/v2/companies/${companyId}/business-entities/delete-or-merge`, {
    data: request,
  });
};

export type BalanceSettings = {
  showBalance: boolean;
  openingYearMonth: string;
  openingSalesBalance: number;
  openingPurchaseBalance: number;
};
export interface BulkRequest extends BalanceSettings {
  businessEntityId: number;
}

export const updateOpeningBalanceBulk = ({
  companyId,
  request,
}: {
  companyId: string;
  request: BulkRequest[];
}) => {
  return API.patch(
    `/api/v2/companies/${companyId}/business-entities/contracts/opening-balances/bulk`,
    {
      data: request,
    },
  );
};
